// ConvertBanner.tsx — Hinweis am Kopf der Anlage-Masken, wenn sie im
// Umwandlungs-Modus laufen (Kontakttyp Firma ↔ Person ändern).
//
// Macht zwei Dinge klar, die man einem vorausgefüllten Anlage-Formular sonst
// nicht ansieht:
//  1. Es entsteht KEIN neuer Kontakt — der bestehende wird geändert.
//  2. Welche Beziehungen ihre Rolle verloren haben und deshalb neu zugeordnet
//     werden wollen.

import * as React from "react";
import { makeStyles, shorthands } from "@fluentui/react-components";
import { BRAND } from "./risikoanalyseStep/tokens";
import { contactTypeLabel } from "../utils/contactTypeLabel";

const useStyles = makeStyles({
  wrap: {
    backgroundColor: "#f7f2f8",
    ...shorthands.border("1px", "solid", "#e2d5e4"),
    ...shorthands.borderRadius("10px"),
    paddingTop: "14px",
    paddingBottom: "14px",
    paddingLeft: "16px",
    paddingRight: "16px",
    marginBottom: "18px",
    display: "flex",
    flexDirection: "column",
    rowGap: "8px",
  },
  head: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    color: BRAND.purple,
  },
  text: {
    margin: 0,
    fontSize: "0.87rem",
    lineHeight: 1.5,
    color: BRAND.ink,
  },
  list: {
    margin: "2px 0 0",
    paddingLeft: "18px",
    fontSize: "0.85rem",
    lineHeight: 1.6,
    color: BRAND.ink,
  },
  prev: { color: BRAND.muted },
});

export function ConvertBanner({
  from,
  relations,
  fields,
}: {
  from: "Firma" | "Person";
  relations?: { name: string; previous: string }[];
  /** Felder des bisherigen Typs, die geleert (und vorher archiviert) werden. */
  fields?: { label: string; value: string }[];
}) {
  const s = useStyles();
  const to = from === "Firma" ? "Person" : "Firma";
  const list = relations ?? [];
  const lost = fields ?? [];

  return (
    <div className={s.wrap}>
      <div className={s.head}>
        <svg width="19" height="19" viewBox="0 0 22 22" fill="none" stroke="currentColor" strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
          <path d="M4 8h11l-3-3M18 14H7l3 3" />
        </svg>
        Umwandlung {contactTypeLabel(from)} → {contactTypeLabel(to)}
      </div>
      <p className={s.text}>
        Dieser Kontakt wird <strong>geändert</strong>, nicht neu angelegt. Kundennummer,
        Verträge, Schäden, Dokumente, Vorgänge und die Verantwortlichkeit bleiben
        erhalten. Die Felder unten sind aus dem bestehenden Kontakt vorausgefüllt —
        bitte prüfen und ergänzen, was beim bisherigen Typ nicht erfasst war.
      </p>
      {lost.length > 0 && (
        <>
          <p className={s.text}>
            {lost.length === 1
              ? "Dieses Feld gehört zum bisherigen Typ und wird geleert"
              : `Diese ${lost.length} Felder gehören zum bisherigen Typ und werden geleert`}
            {" "}— die Werte bleiben im Reiter „Kundentagebuch“ nachlesbar:
          </p>
          <ul className={s.list}>
            {lost.map((f, i) => (
              <li key={i}>
                {f.label} <span className={s.prev}>({f.value})</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {list.length > 0 && (
        <>
          <p className={s.text}>
            {list.length === 1 ? "Diese Beziehung hat" : "Diese Beziehungen haben"} durch
            die Umwandlung {list.length === 1 ? "ihre" : "ihre"} Rolle verloren und
            {list.length === 1 ? " kann" : " können"} anschließend auf der Kontaktseite
            neu zugeordnet werden:
          </p>
          <ul className={s.list}>
            {list.map((r, i) => (
              <li key={i}>
                {r.name} <span className={s.prev}>(vorher: {r.previous})</span>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
